// src/pages/BillingPage.jsx
// --- RELIABLE TABS WITH REAL DATA INTEGRATION ---
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Paper, Typography, Button, CircularProgress, Box, Stack, Grid, Tabs, Alert as MuiAlert, Tab,
  useTheme, alpha, Divider, TablePagination, Skeleton, Chip, Grow, Fade,
  Backdrop, Avatar, List
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AccountBalanceWalletOutlined as AccountBalanceWalletIcon,
  PaymentRounded as PaymentIcon, // Using PaymentIcon for Plans
  AssessmentRounded as UsageHistoryIcon,
  ErrorOutlineRounded as ErrorOutlineIcon,
  RefreshRounded as RefreshIcon,
  CelebrationRounded as CelebrationIcon,
  SettingsOutlined as SettingsIcon,
  ManageSearchOutlined as ManageSearchIcon,
} from '@mui/icons-material';

import { billingService } from '../api/billingService';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';

import SectionCard from '../components/billing/SectionCard';
import CreditBalanceCard from '../components/billing/CreditBalanceCard';
import CurrentSubscriptionDetails from '../components/billing/CurrentSubscriptionDetails';
import UsageSummaryDisplay from '../components/billing/UsageSummaryDisplay';
import TransactionListItem from '../components/billing/TransactionListItem';
import PlanCard from '../components/billing/PlanCard'; // We'll provide this below

// 1. Define Tab Configuration (Stable - IDs must match URL hash values)
const TAB_CONFIG = [
    { id: "overview", label: "Overview", icon: <AccountBalanceWalletIcon /> },
    { id: "plans", label: "Plans & Pricing", icon: <PaymentIcon /> },
    { id: "usage-history", label: "Usage & History", icon: <UsageHistoryIcon /> }
];
const DEFAULT_TAB_ID = TAB_CONFIG[0].id; // "overview"

const BillingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth(); // Assuming useAuth provides the user object
  const {
    accountStatus,
    isLoadingStatus: isLoadingBillingContextStatus,
    statusError: billingContextError,
    forceRefreshAccountStatus,
  } = useBilling();

  // --- State Declarations ---
  const [activeTabId, setActiveTabId] = useState(DEFAULT_TAB_ID); // String ID of the active tab

  const [availablePlans, setAvailablePlans] = useState({ subscription_plans: [], one_time_packages: [] });
  const [creditTransactions, setCreditTransactions] = useState({ items: [], total: 0 });
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageSummaryFetchedForPeriod, setUsageSummaryFetchedForPeriod] = useState(null); // Tracks which period was fetched

  const [pageInitialLoading, setPageInitialLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true); // For fetching plan details
  const [loadingTransactions, setLoadingTransactions] = useState(false); // For fetching transactions
  const [loadingUsage, setLoadingUsage] = useState(false); // For fetching usage summary
  const [loadingCheckout, setLoadingCheckout] = useState(false); // For Stripe actions
  const [isRefreshingManually, setIsRefreshingManually] = useState(false);
  
  const [pageError, setPageError] = useState(null); // For errors fetching plans, distinct from billingContextError
  const lastFailedUsagePeriodRef = useRef(null); // To prevent re-fetching failed usage period immediately
  const lastFailedTransactionPageRef = useRef(null); // To prevent re-fetching failed transaction page immediately
  
  const [selectedUsagePeriod, setSelectedUsagePeriod] = useState("current_month");
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);

  // --- Memoized Values ---
  const freeTierPlanDetails = useMemo(() => {
    if (!availablePlans?.subscription_plans) return null;
    return availablePlans.subscription_plans.find(
      p => p.is_designated_free_tier === true || p.tier_name === "Free Monthly Trial" // Common free tier names
    ) || null;
  }, [availablePlans]);

  const isPotentiallyEligibleForFreeTier = useMemo(() => {
    if (isLoadingBillingContextStatus || !accountStatus) return false; // Wait for context data
    if (!accountStatus.subscription) return true; // No subscription means eligible
    // Eligible if current sub is canceled or incomplete_expired
    return ['canceled', 'incomplete_expired'].includes(accountStatus.subscription.status?.toLowerCase());
  }, [accountStatus, isLoadingBillingContextStatus]);
  
  const userHasActiveSub = useMemo(() => 
    accountStatus?.subscription && 
    ['active', 'trialing'].includes(accountStatus.subscription.status?.toLowerCase()), 
  [accountStatus]);

  // --- Fetch Callbacks ---
  const fetchUsageSummaryCb = useCallback(async (periodToFetch, isRetry = false) => {
    if (!isRetry && periodToFetch === lastFailedUsagePeriodRef.current) return; // Don't auto-retry a known failure
    setLoadingUsage(true);
    const res = await billingService.getUsageSummary(periodToFetch);
    if (res.isError) {
      toast.error(`Usage summary error: ${res.message || 'Unknown'}`);
      setUsageSummary(null); 
      setUsageSummaryFetchedForPeriod(null); // Clear fetched period on error
      lastFailedUsagePeriodRef.current = periodToFetch;
    } else {
      setUsageSummary(res); 
      setUsageSummaryFetchedForPeriod(periodToFetch);
      if (lastFailedUsagePeriodRef.current === periodToFetch) lastFailedUsagePeriodRef.current = null; // Clear if success
    }
    setLoadingUsage(false);
  }, []);

  const fetchCreditTransactions = useCallback(async (page, rowsPerPage, isRetry = false) => {
    if (!isRetry && page === lastFailedTransactionPageRef.current) return;
    setLoadingTransactions(true);
    const res = await billingService.getCreditTransactions(rowsPerPage, page * rowsPerPage);
    if (res.isError) {
      toast.error(`Transactions error (Pg ${page + 1}): ${res.message || 'Unknown'}`);
      // Don't wipe existing transactions if a subsequent page fails, unless it's the first load.
      if (creditTransactions.items.length === 0) setCreditTransactions({ items: [], total: 0 });
      lastFailedTransactionPageRef.current = page;
    } else {
      setCreditTransactions(res || { items: [], total: 0 });
      if (lastFailedTransactionPageRef.current === page) lastFailedTransactionPageRef.current = null;
    }
    setLoadingTransactions(false);
  }, [creditTransactions.items.length]); // Dependency added to ensure correct behavior if items were previously empty

  const fetchAvailablePlans = useCallback(async () => {
    setLoadingPlans(true); 
    setPageError(null); // Clear previous plan errors
    const res = await billingService.getAvailablePlans();
    if (res.isError) {
      setPageError(res.message || "Failed to load plan details.");
      toast.error(`Plans error: ${res.message || 'Unknown'}`);
      setAvailablePlans({ subscription_plans: [], one_time_packages: [] }); // Ensure it's always an object
    } else {
      setAvailablePlans(res || { subscription_plans: [], one_time_packages: [] });
    }
    setLoadingPlans(false);
  }, []);

  // --- Effects ---
  // Initial Page Loading State (depends on billing context and plans)
  useEffect(() => {
    if (!isLoadingBillingContextStatus && !loadingPlans) {
      setPageInitialLoading(false);
    } else {
      setPageInitialLoading(true);
    }
  }, [isLoadingBillingContextStatus, loadingPlans]);

  // Fetch plans on initial mount
  useEffect(() => {
    fetchAvailablePlans();
  }, [fetchAvailablePlans]); // fetchAvailablePlans is memoized

  // EFFECT 1: Synchronize activeTabId FROM URL hash changes (This is the PRIMARY driver for tab state)
  useEffect(() => {
    const hash = location.hash.replace('#', ''); // Get "overview", "plans", etc.
    const targetTab = TAB_CONFIG.find(tab => tab.id === hash);
    const newActiveTabId = targetTab ? targetTab.id : DEFAULT_TAB_ID;

    if (newActiveTabId !== activeTabId) {
      setActiveTabId(newActiveTabId);
    }
    // If hash is empty or invalid, and current activeTabId isn't default, set URL to default tab
    else if ((!hash || !targetTab) && location.hash !== `#${DEFAULT_TAB_ID}`) {
        navigate(`${location.pathname}#${DEFAULT_TAB_ID}`, { replace: true });
    }
  }, [location.hash, activeTabId, navigate]); // Added navigate to deps

  // Data Fetching Effect for "Usage & History" Tab (ID: "usage-history")
  useEffect(() => {
    if (pageInitialLoading || activeTabId !== "usage-history") return;

    // Fetch Usage Summary if needed
    const needsUsageData = !usageSummaryFetchedForPeriod || selectedUsagePeriod !== usageSummaryFetchedForPeriod;
    if (needsUsageData && !loadingUsage) {
      fetchUsageSummaryCb(selectedUsagePeriod);
    }

    // Fetch Transactions if it's the initial load for this tab view or pagination changed
    const isInitialTransactionLoad = creditTransactions.items.length === 0 && creditTransactions.total === 0;
    if (isInitialTransactionLoad && !loadingTransactions) {
      fetchCreditTransactions(transactionPage, transactionRowsPerPage);
    }
  }, [
    activeTabId, pageInitialLoading, selectedUsagePeriod, usageSummaryFetchedForPeriod, 
    fetchUsageSummaryCb, loadingUsage, 
    creditTransactions.items.length, creditTransactions.total, transactionPage, transactionRowsPerPage, 
    fetchCreditTransactions, loadingTransactions
  ]);

  // Checkout Success/Cancel Handler Effect
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const checkoutStatus = queryParams.get('checkout');
    if (checkoutStatus) {
      const action = queryParams.get('action');
      let toastId = `checkout-${Date.now()}`;
      if (checkoutStatus === 'success') {
        let successMessage = "Checkout successful! Your account details are updating.";
        if (action === 'free_tier_activated') successMessage = "Free Tier successfully activated!";
        else if (action === 'subscribed') successMessage = "Subscription successfully activated!";
        else if (action === 'topped_up') successMessage = "Credits successfully purchased!";
        
        toast.success(successMessage, { autoClose: 8000, toastId });
        // Give Stripe webhooks a moment, then refresh critical data
        setTimeout(() => {
            forceRefreshAccountStatus(); 
            fetchAvailablePlans(); 
            if(activeTabId === "usage-history") fetchUsageSummaryCb(selectedUsagePeriod, true); // Force retry for usage
        }, 2000); // Increased delay slightly
      } else if (checkoutStatus === 'cancel') {
        toast.warn("Checkout process was cancelled.", { toastId });
      }
      navigate(location.pathname, { replace: true }); // Clear query params from URL
    }
  }, [location.search, navigate, forceRefreshAccountStatus, activeTabId, selectedUsagePeriod, fetchUsageSummaryCb, fetchAvailablePlans]);

  // --- Event Handlers ---
  // EFFECT 2: Tab click handler -> Updates URL hash. useEffect #1 above handles state update.
  const handleTabChange = (event, newTabId) => {
    if (newTabId !== activeTabId) {
      navigate(`${location.pathname}#${newTabId}`, { replace: true });
    }
  };
  
  const handleNavigateToPlansTab = () => {
    const plansTab = TAB_CONFIG.find(tab => tab.id === "plans");
    if (plansTab) {
      handleTabChange(null, plansTab.id); // Simulate a tab change event
    }
  };

  const handleUsagePeriodChange = (newPeriod) => {
    setSelectedUsagePeriod(newPeriod);
    // Fetching will be triggered by the data fetching useEffect for "usage-history" tab
  };

  const handleTransactionPageChange = (event, newPage) => {
    setTransactionPage(newPage);
    // Fetching will be triggered by the data fetching useEffect,
    // but since it only fetches on initial load, we need to explicitly call here for pagination.
    if (activeTabId === "usage-history") {
      fetchCreditTransactions(newPage, transactionRowsPerPage, newPage === lastFailedTransactionPageRef.current);
    }
  };

  const handleTransactionRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setTransactionRowsPerPage(newRowsPerPage); 
    setTransactionPage(0); // Reset to first page
    if (activeTabId === "usage-history") {
      fetchCreditTransactions(0, newRowsPerPage, 0 === lastFailedTransactionPageRef.current);
    }
  };

  const handleManualRefresh = async () => { 
    setIsRefreshingManually(true);
    toast.info("Refreshing billing data...", {autoClose: 1500});
    const promises = [ forceRefreshAccountStatus(), fetchAvailablePlans() ]; // Core data
    
    if (activeTabId === "usage-history" || !usageSummaryFetchedForPeriod) { // If on tab, or never fetched
        promises.push(fetchUsageSummaryCb(selectedUsagePeriod, true)); // true for isRetry-like behavior
    }
    if (activeTabId === "usage-history") { // If on tab
        promises.push(fetchCreditTransactions(transactionPage, transactionRowsPerPage, true));
    }
    await Promise.allSettled(promises);
    setIsRefreshingManually(false);
  };

  const handleStripeAction = useCallback(async (actionPromise) => { 
    setLoadingCheckout(true);
    const result = await actionPromise;
    if (result.isError) {
        setLoadingCheckout(false); 
        if (result.status === 409) { // Conflict error (e.g., already subscribed)
            toast.info(
                <Box> <Typography gutterBottom>This action may not be available for your account.</Typography> <Button size="small" variant="outlined" color="inherit" onClick={() => {toast.dismiss(); handleManageSubscription();}} sx={{mt:1}}> Manage Your Subscription </Button> </Box>,
                { autoClose: 15000 }
            );
        } else {
          toast.error(result.message || "An error occurred. Please try again.", {autoClose: 7000});
        }
    } else if (result.checkout_url || result.portal_url) {
      toast.success("Redirecting to Stripe's secure portal...", { autoClose: 3000 });
      window.location.href = result.checkout_url || result.portal_url;
      // setLoadingCheckout(false); // Not needed, page will redirect
    } else {
      // This case might happen if the API returns success but no URL (e.g., direct activation without redirect)
      // For now, treat as unexpected.
      toast.error("Unexpected response from server. Please refresh and check your account status.", {autoClose: 7000});
      setLoadingCheckout(false);
    }
  }, []); // Removed handleManageSubscription from deps, it's defined below using this.

  const handleCreateCheckout = useCallback((priceId, isSubscription, quantity = 1) => { 
    if (!priceId) { toast.error("Plan or Package ID is missing. Cannot proceed."); return; }
    // Construct success and cancel URLs based on current page
    const successBaseUrl = `${window.location.origin}${location.pathname}?checkout=success&price_id=${priceId}`;
    const successUrl = isSubscription 
        ? `${successBaseUrl}&action=subscribed` 
        : `${successBaseUrl}&type=topup&qty=${quantity}&action=topped_up`;
    const cancelUrl = `${window.location.origin}${location.pathname}?checkout=cancel`;
    
    const payload = { price_id: priceId, success_url: successUrl, cancel_url: cancelUrl };
    if (!isSubscription && quantity > 0) payload.quantity = quantity;
    
    handleStripeAction(billingService.createCheckoutSession(payload));
  }, [handleStripeAction, location.pathname]);

  const handleActivateFreeTierCheckout = useCallback((priceId) => { 
    if (!priceId) { toast.error("Free Tier Plan ID is missing. Cannot proceed."); return; }
    const successUrl = `${window.location.origin}${location.pathname}?checkout=success&price_id=${priceId}&action=free_tier_activated`;
    const cancelUrl = `${window.location.origin}${location.pathname}?checkout=cancel`;
    handleStripeAction(billingService.createCheckoutSession({ price_id: priceId, success_url: successUrl, cancel_url: cancelUrl }));
  }, [handleStripeAction, location.pathname]);

  const handleManageSubscription = useCallback(() => {
    handleStripeAction(billingService.createCustomerPortalSession());
  }, [handleStripeAction]);


  // --- RENDER LOGIC ---
  if (pageInitialLoading) {
    return ( 
      <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: 'calc(100vh - 120px)', display:'flex', flexDirection:'column' }}> 
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}> 
          <Skeleton variant="text" width={280} height={48} /> 
          <Skeleton variant="rounded" width={130} height={40} sx={{borderRadius:'10px'}}/> 
        </Stack> 
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 3.5, borderRadius:'16px' }} /> 
        <Grid container spacing={3.5}> 
          <Grid item xs={12} md={5} lg={4}><Skeleton variant="rounded" height={280} sx={{borderRadius:'20px'}}/></Grid> 
          <Grid item xs={12} md={7} lg={8}><Skeleton variant="rounded" height={280} sx={{borderRadius:'20px'}}/></Grid> 
          <Grid item xs={12}><Skeleton variant="rounded" height={320} sx={{borderRadius:'20px'}}/></Grid> 
        </Grid> 
      </Box> 
    );
  }
  
  return (
    <Grow in={!pageInitialLoading} timeout={400}>
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 6, minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {/* Header */}
      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{xs:'flex-start', sm:'center'}} mb={4} spacing={{xs:2, sm:1}}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 48, height: 48, borderRadius: '12px' }}>
                <SettingsIcon fontSize="medium"/>
            </Avatar>
            <Typography variant="h4" component="h1" fontWeight="bold">Billing & Account</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
            {user && <Chip label={`Account: ${user.email.split('@')[0]}`} size="small" variant="outlined" sx={{ display: {xs:'none', sm:'flex'}, borderRadius:'8px', color:'text.secondary', borderColor: 'divider' }} avatar={<ManageSearchIcon fontSize="small" sx={{color:'text.disabled'}}/>} />}
            <Button size="medium" onClick={handleManualRefresh} startIcon={isRefreshingManually ? <CircularProgress size={18} color="inherit"/> : <RefreshIcon />} disabled={loadingCheckout || isRefreshingManually} variant="outlined" sx={{textTransform:'none', borderRadius:'10px'}}>
                Refresh
            </Button>
        </Stack>
      </Stack>

      {/* Global Error Alert for Billing Context or Plan Fetching */}
      {(billingContextError || pageError) && (
        <MuiAlert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 3.5, borderRadius: '16px', p:2 }} action={ <Button size="small" onClick={handleManualRefresh} variant="outlined" color="inherit" disabled={isRefreshingManually}>Retry All</Button> }>
          <Typography fontWeight="medium" gutterBottom>Error Loading Billing Information</Typography>
          <Typography variant="body2">{billingContextError || pageError}</Typography>
        </MuiAlert>
      )}

      {/* Tabs Navigation */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '16px', overflow:'hidden', mb: 3.5}}>
        <Tabs 
            value={activeTabId} // Driven by state synced from URL
            onChange={handleTabChange} // Updates URL, which triggers state update via useEffect
            aria-label="billing tabs" 
            variant="fullWidth" 
            indicatorColor="primary" 
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', minHeight: 60, fontWeight: 500, '&.Mui-selected': { fontWeight: 600 } }, '& .MuiTabs-indicator': { height: 3, borderTopLeftRadius: 3, borderTopRightRadius: 3}}}
        >
          {TAB_CONFIG.map(tab => <Tab key={tab.id} value={tab.id} label={tab.label} icon={tab.icon} iconPosition="start" />)}
        </Tabs>
      </Paper>
      
      {/* Tab Content Area - Each tab's content wrapped in Fade */}
      <Box sx={{position: 'relative', flexGrow: 1}}>
        <Fade in={activeTabId === "overview"} timeout={350} mountOnEnter unmountOnExit>
            <Box sx={{ width: '100%'}}>
                <Grid container spacing={3.5}>
                    <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex' }}>
                        <CreditBalanceCard balance={accountStatus?.balance} isLoading={isLoadingBillingContextStatus} onViewPlansClick={handleNavigateToPlansTab} lastTopUpAmount={accountStatus?.last_one_time_purchase_amount}/>
                    </Grid>
                    <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex' }}>
                        <CurrentSubscriptionDetails
                            subscription={accountStatus?.subscription}
                            isLoading={isLoadingBillingContextStatus}
                            isLoadingCheckout={loadingCheckout}
                            onManageSubscription={handleManageSubscription}
                            onActivateFreeTier={handleActivateFreeTierCheckout}
                            onExplorePaidPlans={handleNavigateToPlansTab}
                            isEligibleForFreeTier={isPotentiallyEligibleForFreeTier && !!freeTierPlanDetails}
                            freeTierPlanId={freeTierPlanDetails?.stripe_price_id}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Fade>

        <Fade in={activeTabId === "plans"} timeout={350} mountOnEnter unmountOnExit>
            <Box sx={{ width: '100%'}}>
                {isPotentiallyEligibleForFreeTier && freeTierPlanDetails && !userHasActiveSub && (
                    <MuiAlert severity="success" icon={<CelebrationIcon sx={{fontSize: '1.5rem'}}/>} 
                        sx={{ mb:3.5, p: {xs:2, sm:2.5}, borderRadius: '16px', border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.15)}, ${alpha(theme.palette.success.main, 0.05)})`}}
                        action={ <Button variant="contained" color="success" size="medium" onClick={() => freeTierPlanDetails.stripe_price_id && handleActivateFreeTierCheckout(freeTierPlanDetails.stripe_price_id)} disabled={loadingCheckout || !freeTierPlanDetails.stripe_price_id} sx={{textTransform:'none', fontWeight:'bold', borderRadius:'10px'}}> {loadingCheckout ? <CircularProgress size={22} color="inherit"/> : 'Activate Free Trial'} </Button> }>
                        <Typography variant="h6" fontWeight="medium" sx={{color: theme.palette.success.darker || theme.palette.success.dark}}>You're eligible for our Free Trial!</Typography>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mt:0.5 }}>Get <strong>{freeTierPlanDetails.credits_per_month || 'bonus'} credits</strong> & explore premium features for 30 days.</Typography>
                    </MuiAlert>
                )}
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2.5, mt: (isPotentiallyEligibleForFreeTier && freeTierPlanDetails && !userHasActiveSub) ? 0 : 1 }}>Subscription Plans</Typography>
                {(loadingPlans && (!availablePlans?.subscription_plans || availablePlans.subscription_plans.length === 0)) ? ( 
                    <Grid container spacing={3.5}>{[...Array(3)].map((_,i) => <Grid item xs={12} sm={6} md={4} key={`sub_skel_${i}`}><Skeleton variant="rounded" sx={{minHeight: {xs:380, sm:460}, borderRadius:'20px'}}/></Grid>)}</Grid>
                ) : availablePlans?.subscription_plans && availablePlans.subscription_plans.length > 0 ? ( 
                    <Grid container spacing={3.5}> {availablePlans.subscription_plans.map((plan, index) => plan ? ( <Grid item xs={12} sm={6} md={4} key={plan.stripe_price_id || plan.id || index}> <PlanCard delay={index * 100} plan={plan} onSubscribe={handleCreateCheckout} isLoading={loadingCheckout} isCurrentPlan={accountStatus?.subscription?.stripe_price_id === plan.stripe_price_id && userHasActiveSub} isFreeTierActive={accountStatus?.subscription?.stripe_price_id === freeTierPlanDetails?.stripe_price_id && accountStatus?.subscription?.status === 'trialing'} onActivateFreeTier={ (freeTierPlanDetails?.stripe_price_id === plan.stripe_price_id && isPotentiallyEligibleForFreeTier && plan.stripe_price_id) ? () => handleActivateFreeTierCheckout(plan.stripe_price_id) : undefined } isEligibleForFreeTier={isPotentiallyEligibleForFreeTier && !!freeTierPlanDetails && freeTierPlanDetails?.stripe_price_id === plan.stripe_price_id} onManageInPortal={userHasActiveSub && accountStatus?.subscription?.stripe_price_id === plan.stripe_price_id ? handleManageSubscription : undefined} /> </Grid> ) : null)} </Grid>
                ) : ( <MuiAlert severity="info" sx={{borderRadius:'16px', p:2}}>No subscription plans are currently available. Check back soon!</MuiAlert> )}
                
                <Divider sx={{ my: 5 }} ><Chip label="OR" size="small" variant="outlined"/></Divider>

                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2.5 }}>One-Time Credit Top-ups</Typography>
                {(loadingPlans && (!availablePlans?.one_time_packages || availablePlans.one_time_packages.length === 0)) ? ( 
                    <Grid container spacing={3.5}>{[...Array(2)].map((_,i) => <Grid item xs={12} sm={6} md={4} key={`otp_skel_${i}`}><Skeleton variant="rounded" sx={{minHeight: {xs:320, sm:460}, borderRadius:'20px'}}/></Grid>)}</Grid>
                ) : availablePlans?.one_time_packages && availablePlans.one_time_packages.length > 0 ? (
                    <Grid container spacing={3.5}> {availablePlans.one_time_packages.map((pkg, index) => pkg ? ( <Grid item xs={12} sm={6} md={4} key={pkg.stripe_price_id || pkg.id || index}> <PlanCard delay={index * 100} plan={pkg} onSubscribe={(priceId, _isSub, qty) => handleCreateCheckout(priceId, false, qty)} isLoading={loadingCheckout} /> </Grid> ) : null)} </Grid>
                ) : ( <MuiAlert severity="info" sx={{borderRadius:'16px', p:2}}>No one-time credit purchase options are currently available.</MuiAlert> )}
            </Box>
        </Fade>

        <Fade in={activeTabId === "usage-history"} timeout={350} mountOnEnter unmountOnExit>
            <Box sx={{ width: '100%'}}>
                 <UsageSummaryDisplay 
                    summary={usageSummary} 
                    isLoading={loadingUsage}
                    onPeriodChange={handleUsagePeriodChange} 
                    selectedPeriod={selectedUsagePeriod} 
                    // Add a retry handler for usage summary if desired
                    onRetryFetch={() => fetchUsageSummaryCb(selectedUsagePeriod, true)}
                    hasError={lastFailedUsagePeriodRef.current === selectedUsagePeriod}
                />
                <Divider sx={{ mt: 3.5, mb: 3 }}><Chip label="Transaction Details" size="small" variant="outlined"/></Divider>
                <SectionCard title="Credit Transaction History" icon={<UsageHistoryIcon />} contentSx={{p:0, pt:0}}>
                    {(loadingTransactions && creditTransactions.items.length === 0 && lastFailedTransactionPageRef.current === null) ? ( 
                        <Stack alignItems="center" justifyContent="center" sx={{minHeight: 300, p:3}}><CircularProgress sx={{mb:2}}/><Typography>Loading transactions...</Typography></Stack>
                    ) : (!loadingTransactions && creditTransactions.items.length === 0 && lastFailedTransactionPageRef.current === null) ? ( 
                        <Stack alignItems="center" justifyContent="center" sx={{minHeight: 300, p:3}}><UsageHistoryIcon sx={{fontSize: 64, color: 'text.disabled', mb:2}}/><Typography variant="h6" color="text.secondary">No Transactions Yet</Typography><Typography variant="body2" color="text.disabled">Your credit purchases and usage deductions will appear here.</Typography></Stack>
                    ) : (lastFailedTransactionPageRef.current !== null && creditTransactions.items.length === 0) ? ( 
                        <Stack alignItems="center" justifyContent="center" sx={{minHeight: 300, p:3}}><ErrorOutlineIcon sx={{fontSize: 64, color: theme.palette.error.main, mb:2}}/><Typography variant="h6" color="error.dark">Failed to Load Transactions</Typography><Typography variant="body2" color="text.secondary" sx={{mb:1.5}}>Could not retrieve transaction history for page {lastFailedTransactionPageRef.current + 1}.</Typography><Button onClick={() => fetchCreditTransactions(lastFailedTransactionPageRef.current, transactionRowsPerPage, true)} variant="outlined" color="error">Retry Loading Page</Button></Stack>
                    ): ( 
                        <Box> 
                            <List sx={{px: {xs:1.5, sm:2}, py:1, pt:0}}>{creditTransactions.items.map(tx => ( <TransactionListItem key={tx.id} transaction={tx} /> ))}</List> 
                            {creditTransactions.total > 0 && <TablePagination component="div" count={creditTransactions.total} page={transactionPage} onPageChange={handleTransactionPageChange} rowsPerPage={transactionRowsPerPage} onRowsPerPageChange={handleTransactionRowsPerPageChange} rowsPerPageOptions={[5, 10, 25, 50]} sx={{ borderTop: 1, borderColor: 'divider', backgroundColor: alpha(theme.palette.background.default, 0.6), borderRadius: '0 0 16px 16px' }} />} 
                        </Box> 
                    )}
                </SectionCard>
            </Box>
        </Fade>
      </Box>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 101, backdropFilter: 'blur(5px)', bgcolor: alpha(theme.palette.common.black, 0.65) }} open={loadingCheckout}>
        <Paper sx={{ p: {xs:3.5, sm:5}, display: 'flex', alignItems: 'center', borderRadius: '20px', flexDirection: 'column', textAlign: 'center', boxShadow: theme.shadows[16], background: theme.palette.background.paper, minWidth: {xs: '85vw', sm: 400} }}> 
            <CircularProgress color="primary" sx={{ mb: 3.5 }} size={60} thickness={3.5}/> 
            <Typography variant="h5" component="div" sx={{mb: 1, fontWeight:500, color: 'text.primary'}}>Securely Connecting to Stripe...</Typography> 
            <Typography variant="body1" color="text.secondary">Please wait while we redirect you. Do not close or refresh this page.</Typography> 
        </Paper>
      </Backdrop>
    </Box>
    </Grow>
  );
};

export default BillingPage;